<script lang="ts">
  import type { Api, PageSnapshot } from "../lib/api";
  import { Dialog, DialogContent, DialogHeader, DialogTitle, Icon } from "@price-check/ui";
  import { ExternalLink } from "lucide";
  import Pill from "../lib/ui/Pill.svelte";
  import Banner from "../lib/ui/Banner.svelte";
  import Spinner from "../lib/ui/Spinner.svelte";
  import { loginLabel, loginTone } from "../lib/snapshot-status";
  import { kindLabel } from "../lib/snapshot-groups";

  let { api, id, onClose }: { api: Api; id: string; onClose: () => void } = $props();

  let snap = $state<PageSnapshot | null>(null);
  let error = $state("");

  $effect(() => {
    const a = api;
    const snapId = id;
    let alive = true;
    snap = null;
    error = "";
    a.getSnapshot(snapId)
      .then((s) => {
        if (alive) snap = s;
      })
      .catch((e) => {
        if (alive) error = e instanceof Error ? e.message : "failed to load";
      });
    return () => {
      alive = false;
    };
  });
</script>

<Dialog
  open={true}
  onOpenChange={(v) => {
    if (!v) onClose();
  }}
>
  <DialogContent class="max-w-[min(1120px,95vw)]">
    <DialogHeader>
      <DialogTitle>
        {snap ? (snap.vendor_name ?? snap.vendor_id) : "Snapshot"}
      </DialogTitle>
    </DialogHeader>

    {#if error}
      <Banner tone="danger">{error}</Banner>
    {:else if !snap}
      <Spinner label="Loading snapshot…" />
    {:else}
      <div class="flex flex-wrap items-center gap-3">
        <Pill tone={loginTone(snap.login_state)}>{loginLabel(snap.login_state)}</Pill>
        <Pill tone="indigo">{kindLabel(snap.page_kind)}</Pill>
        <a
          href={snap.url}
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex min-w-0 items-center gap-1.5 text-sm text-brand-strong hover:underline"
          title={snap.url}
        >
          <span class="min-w-0 max-w-[42ch] overflow-hidden text-ellipsis whitespace-nowrap">{snap.url}</span>
          <Icon icon={ExternalLink} size={13} />
        </a>
      </div>
      {#if snap.markers_note}
        <p class="m-0 text-xs text-muted">{snap.markers_note}</p>
      {/if}
      <iframe
        title="Rendered snapshot of {snap.url}"
        srcdoc={snap.html ?? ""}
        sandbox=""
        referrerpolicy="no-referrer"
        class="min-h-[68vh] w-full rounded-md border border-border bg-white"
      ></iframe>
      <p class="m-0 text-2xs text-muted">
        Static render only (scripts + same-origin disabled). Captured
        {new Date(snap.created_at).toLocaleString()}
        {snap.user_email ? `by ${snap.user_email}` : ""}.
      </p>
    {/if}
  </DialogContent>
</Dialog>
